import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { Pagination } from '../../components/ui/Pagination'
import { useClientPagination } from '../../hooks/useClientPagination'

type Status = 'registered' | 'proposed' | 'accepted' | 'rejected' | 'in_progress' | 'done'
type Platform = 'instagram' | 'tiktok' | 'youtube' | 'facebook' | 'twitter' | 'altele'

interface Influencer {
  id: number
  b4lId: string | null
  platform: Platform
  handle: string
  contactEmail: string | null
  /* Datele de livrare. Stau aici, nu pe cerere: a doua oară adresa se completează singură. */
  numeDestinatar?: string | null
  telefon?: string | null
  judet?: string | null
  localitate?: string | null
  strada?: string | null
  numarStrada?: string | null
  codPostal?: string | null
  followers: number | null
  category: string | null
  status: Status
  budget: string | null
  notes: string | null
  lastContactAt: string | null
  createdAt: string
}

const PLATFORM_LABELS: Record<Platform, string> = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  facebook: 'Facebook',
  twitter: 'Twitter / X',
  altele: 'Altă platformă',
}

const STATUS_LABELS: Record<Status, string> = {
  registered: 'Înregistrat',
  proposed: 'Propunere trimisă',
  accepted: 'Acceptat',
  rejected: 'Refuzat',
  in_progress: 'În derulare',
  done: 'Finalizat',
}

const STATUS_COLORS: Record<Status, string> = {
  registered: 'bg-b4l-bg text-b4l-text-muted',
  proposed: 'bg-b4l-blue-light text-b4l-blue-dark',
  accepted: 'bg-b4l-purple-light text-b4l-purple-dark',
  rejected: 'bg-b4l-red-light text-b4l-red-dark',
  in_progress: 'bg-b4l-amber-light text-b4l-amber-dark',
  done: 'bg-b4l-green-light text-b4l-green-dark',
}

export function InfluenceriPage() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('marketing.influencers:write')

  const [createOpen, setCreateOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<Influencer | null>(null)
  const [sendTarget, setSendTarget] = useState<Influencer | null>(null)
  const [statusFilter, setStatusFilter] = useState<Status | ''>('')

  const query = useQuery<{ influencers: Influencer[] }>({
    queryKey: ['influencers'],
    queryFn: async () =>
      (await api.get<{ influencers: Influencer[] }>('/marketing/influencers')).data,
  })

  const createMutation = useMutation({
    mutationFn: async (data: Partial<Influencer>) =>
      (await api.post('/marketing/influencers', data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['influencers'] })
      toast.success('Influencer adăugat')
      setCreateOpen(false)
    },
    onError: showError,
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<Influencer> }) =>
      (await api.patch(`/marketing/influencers/${id}`, data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['influencers'] })
      toast.success('Salvat')
    },
    onError: showError,
  })

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: Status }) =>
      (await api.patch(`/marketing/influencers/${id}/status`, { status })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['influencers'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
    onError: showError,
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/marketing/influencers/${id}`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['influencers'] })
      toast.success('Șters')
    },
    onError: showError,
  })

  const all = useMemo(() => query.data?.influencers ?? [], [query.data])
  const filtered = useMemo(
    () => (statusFilter ? all.filter((i) => i.status === statusFilter) : all),
    [all, statusFilter],
  )
  const pag = useClientPagination(filtered, 10)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Influenceri</h1>
        {canWrite && <Button onClick={() => setCreateOpen(true)}>+ Influencer nou</Button>}
      </div>

      <Card description="Registrul de influenceri și pipeline-ul de propuneri. Email de propunere → B4L-INFLU-XXXXXX, răspunsurile sunt detectate automat în Inbox.">
        <div className="mb-3 flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as Status | '')}
            className="rounded border border-b4l-border bg-white px-3 py-1.5 text-sm"
          >
            <option value="">Toate stările</option>
            {(Object.keys(STATUS_LABELS) as Status[]).map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]}
              </option>
            ))}
          </select>
          <span className="ml-auto text-xs text-b4l-text-muted">
            {filtered.length} / {all.length}
          </span>
        </div>

        {query.isLoading ? (
          <p className="text-sm text-b4l-text-muted">Se încarcă...</p>
        ) : filtered.length === 0 ? (
          <div className="rounded border border-dashed border-b4l-border p-8 text-center text-sm text-b4l-text-muted">
            Niciun influencer{statusFilter ? ` cu starea „${STATUS_LABELS[statusFilter]}”` : ''}.
            {canWrite && !statusFilter && ' Apasă "+ Influencer nou".'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                  <th className="py-2 pr-3 font-medium">ID B4L</th>
                  <th className="py-2 pr-3 font-medium">Platformă</th>
                  <th className="py-2 pr-3 font-medium">Utilizator</th>
                  <th className="py-2 pr-3 font-medium text-right">Urmăritori</th>
                  <th className="py-2 pr-3 font-medium">Email</th>
                  <th className="py-2 pr-3 font-medium">Categorie</th>
                  <th className="py-2 pr-3 font-medium text-right">Buget</th>
                  <th className="py-2 pr-3 font-medium">Stare</th>
                  <th className="py-2 pr-3 font-medium">Acțiuni</th>
                </tr>
              </thead>
              <tbody>
                {pag.paginated.map((i) => (
                  <tr key={i.id} className="border-b border-b4l-border last:border-0">
                    <td className="py-2 pr-3 font-mono text-[11px] text-b4l-purple-dark">
                      {i.b4lId ?? '—'}
                    </td>
                    <td className="py-2 pr-3 text-xs">{PLATFORM_LABELS[i.platform]}</td>
                    <td className="py-2 pr-3 font-mono text-xs">{i.handle}</td>
                    <td className="py-2 pr-3 text-right font-mono text-xs">
                      {i.followers ? i.followers.toLocaleString('ro-RO') : '—'}
                    </td>
                    <td className="py-2 pr-3 font-mono text-[11px]">{i.contactEmail ?? '—'}</td>
                    <td className="py-2 pr-3 text-xs">{i.category ?? '—'}</td>
                    <td className="py-2 pr-3 text-right font-mono text-xs">
                      {i.budget ? Number(i.budget).toFixed(0) : '—'}
                    </td>
                    <td className="py-2 pr-3">
                      {canWrite ? (
                        <select
                          value={i.status}
                          onChange={(e) =>
                            updateStatusMutation.mutate({
                              id: i.id,
                              status: e.target.value as Status,
                            })
                          }
                          className={`rounded px-2 py-0.5 text-[11px] ${STATUS_COLORS[i.status]}`}
                        >
                          {(Object.keys(STATUS_LABELS) as Status[]).map((s) => (
                            <option key={s} value={s}>
                              {STATUS_LABELS[s]}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span
                          className={`rounded px-2 py-0.5 text-[11px] ${STATUS_COLORS[i.status]}`}
                        >
                          {STATUS_LABELS[i.status]}
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <div className="flex gap-1">
                        {canWrite &&
                          i.contactEmail &&
                          i.status !== 'done' &&
                          i.status !== 'rejected' && (
                            <Button
                              onClick={() => setSendTarget(i)}
                              className="!px-2 !py-1 !text-xs"
                            >
                              {i.b4lId ? 'Re-trimite' : 'Propunere'}
                            </Button>
                          )}
                        {canWrite && (
                          <Button
                            variant="secondary"
                            onClick={() => setEditTarget(i)}
                            className="!px-2 !py-1 !text-xs"
                          >
                            Editează
                          </Button>
                        )}
                        {canWrite && !i.b4lId && (
                          <Button
                            variant="danger"
                            onClick={() => {
                              if (confirm(`Ștergi ${i.handle}?`)) deleteMutation.mutate(i.id)
                            }}
                            className="!px-2 !py-1 !text-xs"
                          >
                            Șterge
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-3">
          <Pagination
            page={pag.page}
            pageSize={pag.pageSize}
            total={pag.total}
            onPageChange={pag.setPage}
            onPageSizeChange={pag.setPageSize}
            itemLabel="influenceri"
          />
        </div>
      </Card>

      {createOpen && (
        <InfluencerFormModal
          title="Influencer nou"
          submitLabel="Adaugă"
          onClose={() => setCreateOpen(false)}
          onSubmit={(data) => createMutation.mutate(data)}
          submitting={createMutation.isPending}
        />
      )}

      {editTarget && (
        <InfluencerFormModal
          title={`Editează ${editTarget.handle}`}
          submitLabel="Salvează"
          initial={editTarget}
          onClose={() => setEditTarget(null)}
          onSubmit={(data) =>
            updateMutation.mutate(
              { id: editTarget.id, data },
              { onSuccess: () => setEditTarget(null) },
            )
          }
          submitting={updateMutation.isPending}
        />
      )}

      {sendTarget && (
        <SendProposalModal
          influencer={sendTarget}
          onClose={() => setSendTarget(null)}
          onSent={() => {
            queryClient.invalidateQueries({ queryKey: ['influencers'] })
            queryClient.invalidateQueries({ queryKey: ['dashboard'] })
            setSendTarget(null)
          }}
        />
      )}
    </div>
  )
}

interface FormProps {
  title: string
  submitLabel: string
  initial?: Influencer
  onClose: () => void
  onSubmit: (data: Partial<Influencer>) => void
  submitting: boolean
}

function InfluencerFormModal({
  title,
  submitLabel,
  initial,
  onClose,
  onSubmit,
  submitting,
}: FormProps) {
  const [platform, setPlatform] = useState<Platform>(initial?.platform ?? 'instagram')
  const [handle, setHandle] = useState(initial?.handle ?? '')
  const [contactEmail, setContactEmail] = useState(initial?.contactEmail ?? '')
  const [followers, setFollowers] = useState(initial?.followers ? String(initial.followers) : '')
  const [category, setCategory] = useState(initial?.category ?? '')
  const [budget, setBudget] = useState(initial?.budget ?? '')
  const [notes, setNotes] = useState(initial?.notes ?? '')
  /*
    LIVRAREA. FAN Courier cere adresa pe bucăți — județ, localitate, stradă, număr —, nu ca text.
    Mesajele reale arată de ce: „Alea câmpul moșilor nr 4, DownTown Fitness 2 Obor, București" e
    un singur șir, iar cineva tot trebuie să-l despartă. Mai bine o dată, aici, decât la fiecare
    colet.
  */
  const [numeDestinatar, setNumeDestinatar] = useState(initial?.numeDestinatar ?? '')
  const [telefon, setTelefon] = useState(initial?.telefon ?? '')
  const [judet, setJudet] = useState(initial?.judet ?? '')
  const [localitate, setLocalitate] = useState(initial?.localitate ?? '')
  const [strada, setStrada] = useState(initial?.strada ?? '')
  const [numarStrada, setNumarStrada] = useState(initial?.numarStrada ?? '')
  const [codPostal, setCodPostal] = useState(initial?.codPostal ?? '')

  const submit = () => {
    if (!handle.trim()) {
      toast.error('Completează numele de utilizator (@handle).')
      return
    }
    onSubmit({
      platform,
      handle: handle.trim(),
      contactEmail: contactEmail.trim() || null,
      followers: followers ? Number(followers) : null,
      category: category.trim() || null,
      budget: budget ? Number(budget) : null,
      notes: notes.trim() || null,
      numeDestinatar: numeDestinatar.trim() || null,
      telefon: telefon.trim() || null,
      judet: judet.trim() || null,
      localitate: localitate.trim() || null,
      strada: strada.trim() || null,
      numarStrada: numarStrada.trim() || null,
      codPostal: codPostal.trim() || null,
    } as Partial<Influencer>)
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Anulează
          </Button>
          <Button onClick={submit} loading={submitting}>
            {submitLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Platformă</label>
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value as Platform)}
              className="w-full rounded border border-b4l-border px-3 py-2 text-sm"
            >
              {Object.entries(PLATFORM_LABELS).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </div>
          <Field
            label="Nume de utilizator (@handle)"
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            hint="ex: @nutritionist_ro"
          />
        </div>
        <Field
          label="Email contact"
          type="email"
          value={contactEmail ?? ''}
          onChange={(e) => setContactEmail(e.target.value)}
          hint="Folosit pentru trimiterea propunerii de colaborare"
        />
        <div className="grid grid-cols-3 gap-3">
          <Field
            label="Urmăritori"
            type="number"
            value={followers}
            onChange={(e) => setFollowers(e.target.value)}
          />
          <Field
            label="Categorie"
            value={category ?? ''}
            onChange={(e) => setCategory(e.target.value)}
            hint="ex: fitness, nutriție"
          />
          <Field
            label="Buget (RON)"
            type="number"
            step="0.01"
            value={String(budget ?? '')}
            onChange={(e) => setBudget(e.target.value)}
          />
        </div>
        {/*
          Livrarea, într-un bloc separat: sunt datele care pleacă pe AWB, nu despre colaborare.
          Rămân aici după primul colet, deci al doilea nu mai cere nimic.
        */}
        <div className="rounded-lg border border-b4l-border p-3">
          <p className="mb-2 text-xs font-medium text-b4l-text">
            Livrare — se completează o dată, la primul colet
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Nume destinatar"
              value={numeDestinatar ?? ''}
              onChange={(e) => setNumeDestinatar(e.target.value)}
              hint="numele de pe colet, nu handle-ul"
            />
            <Field
              label="Telefon"
              value={telefon ?? ''}
              onChange={(e) => setTelefon(e.target.value)}
            />
            <Field label="Județ" value={judet ?? ''} onChange={(e) => setJudet(e.target.value)} />
            <Field
              label="Localitate"
              value={localitate ?? ''}
              onChange={(e) => setLocalitate(e.target.value)}
            />
            <Field
              label="Stradă"
              value={strada ?? ''}
              onChange={(e) => setStrada(e.target.value)}
            />
            <Field
              label="Număr"
              value={numarStrada ?? ''}
              onChange={(e) => setNumarStrada(e.target.value)}
            />
            <Field
              label="Cod poștal"
              value={codPostal ?? ''}
              onChange={(e) => setCodPostal(e.target.value)}
            />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Note</label>
          <textarea
            value={notes ?? ''}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full rounded border border-b4l-border px-3 py-2 text-sm"
          />
        </div>
        {initial?.b4lId && (
          <div className="rounded bg-b4l-purple-light p-2 text-xs text-b4l-purple-dark">
            <span className="font-mono">{initial.b4lId}</span> — răspunsurile la acest ID sunt
            corelate automat în Inbox.
          </div>
        )}
      </div>
    </Modal>
  )
}

interface EmailPreview {
  to: string
  subject: string
  body: string
}

function SendProposalModal({
  influencer,
  onClose,
  onSent,
}: {
  influencer: Influencer
  onClose: () => void
  onSent: () => void
}) {
  const previewQuery = useQuery<EmailPreview>({
    queryKey: ['influ-preview', influencer.id],
    queryFn: async () =>
      (await api.get<EmailPreview>(`/marketing/influencers/${influencer.id}/email-preview`)).data,
  })

  const sendMutation = useMutation({
    mutationFn: async () =>
      (await api.post(`/marketing/influencers/${influencer.id}/send-proposal`)).data,
    onSuccess: (data: { b4lId: string }) => {
      toast.success(`Email pus în coadă (${data.b4lId})`)
      onSent()
    },
    onError: showError,
  })

  return (
    <Modal
      open
      onClose={onClose}
      title={`Propunere către ${influencer.handle}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Anulează
          </Button>
          <Button onClick={() => sendMutation.mutate()} loading={sendMutation.isPending}>
            Confirmă trimiterea
          </Button>
        </>
      }
    >
      {previewQuery.isLoading ? (
        <p className="text-sm text-b4l-text-muted">Se generează previzualizarea…</p>
      ) : previewQuery.data ? (
        <div className="space-y-3 text-sm">
          <div>
            <span className="text-xs text-b4l-text-muted">Către:</span>{' '}
            <span className="font-mono">{previewQuery.data.to}</span>
          </div>
          <div>
            <span className="text-xs text-b4l-text-muted">Subiect:</span>{' '}
            <span>
              <span className="font-mono text-b4l-purple-dark">[B4L-INFLU-XXXXXX] </span>
              {previewQuery.data.subject}
            </span>
          </div>
          <div>
            <div className="mb-1 text-xs text-b4l-text-muted">Conținut:</div>
            <pre className="whitespace-pre-wrap rounded border border-b4l-border bg-b4l-bg-soft p-3 font-sans text-xs">
              {previewQuery.data.body}
            </pre>
          </div>
        </div>
      ) : (
        <p className="text-sm text-b4l-red">
          Previzualizarea nu a putut fi generată. Închide fereastra și încearcă din nou.
        </p>
      )}
    </Modal>
  )
}

function showError(err: unknown) {
  const message =
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? 'Eroare'
  toast.error(message)
}
